import { useState, useCallback } from 'react';

export const FREE_TRIAL_LIMIT = 3;

export interface FreeTrialGate {
  used: number;
  remaining: number;
  exhausted: boolean;
  limit: number;
  /** Call before a generation — returns true if allowed, false if exhausted */
  consume: () => boolean;
  /** Dots array for UI: [{active: bool}] */
  dots: { active: boolean }[];
}

// ── Admin bypass helper ───────────────────────────────────────────────────────
function isAdminLoggedIn(): boolean {
  try {
    const raw = localStorage.getItem('openclaw_admin_session');
    if (!raw) return false;
    const user = JSON.parse(raw);
    return user?.isAdmin === true;
  } catch {
    return false;
  }
}

export function useFreeTrialGate(toolKey: string, limit = FREE_TRIAL_LIMIT): FreeTrialGate {
  const storageKey = `trial_v1_${toolKey}`;
  const adminMode = isAdminLoggedIn();

  const [used, setUsed] = useState<number>(() => {
    // Admin always shows 0 usage
    if (isAdminLoggedIn()) return 0;
    try {
      return parseInt(localStorage.getItem(storageKey) ?? '0', 10) || 0;
    } catch {
      return 0;
    }
  });

  // For admin: never exhausted, unlimited remaining
  const effectiveUsed = adminMode ? 0 : used;
  const exhausted = adminMode ? false : effectiveUsed >= limit;
  const remaining = adminMode ? 9999 : Math.max(0, limit - effectiveUsed);

  const consume = useCallback((): boolean => {
    if (isAdminLoggedIn()) return true; // always allow
    if (used >= limit) return false;
    const next = used + 1;
    setUsed(next);
    try {
      localStorage.setItem(storageKey, String(next));
    } catch { /* noop */ }
    return true;
  }, [used, limit, storageKey]);

  const dots = Array.from({ length: limit }, (_, i) => ({ active: adminMode ? false : i < effectiveUsed }));

  return { used: effectiveUsed, remaining, exhausted, limit, consume, dots };
}
